/**
 * Based on https://github.com/gkjohnson/threejs-sandbox/blob/master/screenSpaceReflectionsPass/SSRRPass.js
 * MIT License
 * @author Garrett Johnson / http://gkjohnson.github.io/
 *
 * Approach from
 * http://jcgt.org/published/0003/04/04/paper.pdf
 * https://github.com/kode80/kode80SSR
 *
 * Modifications copyright Higharc Inc. 2019
 */
import { assertIsNotUndefined } from "common/utils/assertOfType";
import * as THREE from "three";
import { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import type { DepthNormalPass } from "./DepthNormalPass";
import {
  screenSpaceViewNormalDefineDefaults,
  screenSpaceViewNormalGLSL,
  screenSpaceViewNormalPrepareShader,
  screenSpaceViewNormalUniformDefaults,
  screenSpaceViewPositionDefineDefaults,
  screenSpaceViewPositionGLSL,
  screenSpaceViewPositionPrepareShader,
  screenSpaceViewPositionUniformDefaults,
} from "./screenSpaceHelpersGLSL";
import { UVVertexShader } from "./UVVertexShader";

export interface SSRRPassOptions {
  colorSrcColorSpace: THREE.ColorSpace;
}

export interface ReflectionParams {
  steps: number;
  stride: number;
  fineGrainedStride: number;
  jitter: number;
  assumedThickness: number;
}

export const defaultReflectionParams: ReflectionParams = {
  steps: 15,
  stride: 16,
  fineGrainedStride: 4,
  jitter: 1.0,
  assumedThickness: 12.0,
};

export class SSRRPass extends EffectComposerPass {
  static OUTPUT = {
    CombineWithReadBuffer: 0, // Combines reflection with read buffer.
    ReflectionBuffer: 1, // Outputs reflected pixel.
  };

  // Use constantThickness with scenes that contain double-sided geometry or that don't have backfaces.
  static BACKFACE_MODE = {
    RenderBackfaceDepth: 0,
    ConstantThickness: 1,
  };

  assumedThickness: number = defaultReflectionParams.assumedThickness;

  reflectionBuffer: THREE.WebGLRenderTarget;

  // Random seed to use on the next render. Should be between 0 and 1.
  randomSeed = 0;

  scene: THREE.Scene | undefined;
  camera: THREE.Camera;

  jitter: number = defaultReflectionParams.jitter;

  depthNormalPass: DepthNormalPass;

  private _steps: number = defaultReflectionParams.steps;
  private _stride: number = defaultReflectionParams.stride;
  private _fineGrainedStride: number = defaultReflectionParams.fineGrainedStride;
  private _backfaceMode: number;
  private _output: number;

  private renderTargetScale: number = 0.5;

  private _prevClearColor: THREE.Color;

  private backfaceDepthMaterial: THREE.Material;
  private outputMaterial: THREE.ShaderMaterial;
  private fsQuad: FullScreenQuad;

  private colorSrcBuffer: THREE.WebGLRenderTarget;
  private backfaceDepthBuffer: THREE.WebGLRenderTarget;

  constructor(
    scene: THREE.Scene | undefined,
    camera: THREE.Camera,
    depthNormalPass: DepthNormalPass,
    options: Partial<SSRRPassOptions> = {},
  ) {
    super();

    this._output = SSRRPass.OUTPUT.CombineWithReadBuffer;
    this._backfaceMode = SSRRPass.BACKFACE_MODE.ConstantThickness;

    this.scene = scene;
    this.camera = camera;
    this.depthNormalPass = depthNormalPass;

    this._prevClearColor = new THREE.Color();

    // render targets
    this.colorSrcBuffer = new THREE.WebGLRenderTarget(256, 256, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      colorSpace: options.colorSrcColorSpace || THREE.SRGBColorSpace,
    });

    this.backfaceDepthBuffer = new THREE.WebGLRenderTarget(256, 256, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
    });
    this.backfaceDepthBuffer.texture.name = "SSRRPass.BackfaceDepth";
    this.backfaceDepthBuffer.texture.generateMipmaps = false;
    this.backfaceDepthMaterial = this.createLinearDepthMaterial();
    this.backfaceDepthMaterial.side = THREE.BackSide;

    this.reflectionBuffer = new THREE.WebGLRenderTarget(256, 256, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
    });

    this.outputMaterial = this.getOutputMaterial();
    this.fsQuad = new FullScreenQuad(this.outputMaterial);

    this.updateOutputMaterial();
  }

  get steps(): number {
    return this._steps;
  }
  set steps(value: number) {
    if (this._steps === value) {
      return;
    }
    this._steps = value;
    this.updateOutputMaterial();
  }

  get stride(): number {
    return this._stride;
  }
  set stride(value: number) {
    if (this._stride === value) {
      return;
    }
    this._stride = value;
    this.updateOutputMaterial();
  }

  get fineGrainedStride(): number {
    return this._fineGrainedStride;
  }
  set fineGrainedStride(value: number) {
    if (this._fineGrainedStride === value) {
      return;
    }
    this._fineGrainedStride = value;
    this.updateOutputMaterial();
  }

  get backfaceMode() {
    return this._backfaceMode;
  }
  set backfaceMode(value) {
    if (this._backfaceMode === value) {
      return;
    }
    this._backfaceMode = value;
    this.updateOutputMaterial();
  }

  get output() {
    return this._output;
  }
  set output(value) {
    if (this._output === value) {
      return;
    }
    this._output = value;
    this.updateOutputMaterial();
  }

  dispose() {
    this.colorSrcBuffer.dispose();
    this.backfaceDepthBuffer.dispose();
    this.reflectionBuffer.dispose();
  }

  setSize(width: number, height: number) {
    super.setSize(width, height);
    width *= this.renderTargetScale;
    height *= this.renderTargetScale;

    this.colorSrcBuffer.setSize(width, height);
    this.backfaceDepthBuffer.setSize(width, height);
    this.reflectionBuffer.setSize(width, height);
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null) {
    // Set the clear state
    renderer.getClearColor(this._prevClearColor);
    const prevClearAlpha = renderer.getClearAlpha();
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setClearColor(new THREE.Color(0, 0, 0), 0);

    assertIsNotUndefined(this.scene);

    const prevOverride = this.scene.overrideMaterial;

    // TODO: Maybe reuse prev frame for computing reflections?

    // Source color pass
    this.scene.overrideMaterial = null;
    renderer.setRenderTarget(this.colorSrcBuffer);
    renderer.clear();
    renderer.render(this.scene, this.camera);

    if (this.backfaceMode === SSRRPass.BACKFACE_MODE["RenderBackfaceDepth"]) {
      this.scene.overrideMaterial = this.backfaceDepthMaterial;
      renderer.setRenderTarget(this.backfaceDepthBuffer);
      renderer.render(this.scene, this.camera);
    }
    this.scene.overrideMaterial = prevOverride;

    // Composite
    const uniforms = this.outputMaterial.uniforms;

    uniforms["randomSeed"].value = this.randomSeed;

    screenSpaceViewPositionPrepareShader(this.outputMaterial, this.camera, this.depthNormalPass);
    screenSpaceViewNormalPrepareShader(this.outputMaterial, this.depthNormalPass);

    uniforms.sourceBuffer.value = this.colorSrcBuffer.texture;
    uniforms.backfaceDepthBuffer.value = this.backfaceDepthBuffer.texture;
    uniforms.resolution.value.set(this.colorSrcBuffer.width, this.colorSrcBuffer.height);
    uniforms.intensity.value = 0.5;
    uniforms.jitter.value = this.jitter;
    uniforms.assumedThickness.value = this.assumedThickness;

    if (this.output === SSRRPass.OUTPUT["CombineWithReadBuffer"]) {
      renderer.setRenderTarget(writeBuffer);
      renderer.clear();
      this.fsQuad.render(renderer);
    } else {
      renderer.setRenderTarget(this.reflectionBuffer);
      renderer.clear();
      this.fsQuad.render(renderer);
    }

    // Restore renderer settings
    renderer.setClearColor(this._prevClearColor, prevClearAlpha);
    renderer.autoClear = prevAutoClear;
  }

  createLinearDepthMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      vertexShader: `
        varying vec3 vViewPosition;
        #ifndef FLAT_SHADED
          varying vec3 vNormal;
        #endif
        void main() {
          #include <beginnormal_vertex>
          #include <defaultnormal_vertex>
          #include <begin_vertex>
          #include <project_vertex>
          vViewPosition = mvPosition.xyz;
        }
      `,

      fragmentShader: `
        varying vec3 vViewPosition;
        void main() {
          gl_FragColor = vec4(vViewPosition.z);
        }
      `,

      name: "SSRRPass linearDepthMaterial",
    });
  }

  private updateOutputMaterial() {
    let backfaceDepthBufferEnabled = 0;
    if (this._backfaceMode === SSRRPass.BACKFACE_MODE["RenderBackfaceDepth"]) {
      backfaceDepthBufferEnabled = 1;
    }
    this.outputMaterial.defines.BACKFACE_DEPTH_BUFFER_ENABLED = backfaceDepthBufferEnabled;
    this.outputMaterial.defines.MAX_STEPS = Math.floor(this._steps);
    this.outputMaterial.defines.STRIDE = Math.floor(this._stride);
    this.outputMaterial.defines.FINE_GRAINED_STRIDE = Math.floor(this._fineGrainedStride);
    this.outputMaterial.defines.OUTPUT = this._output;
    this.outputMaterial.needsUpdate = true;
  }

  getOutputMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      name: "SSRRPass outputMaterial",
      defines: {
        ...screenSpaceViewPositionDefineDefaults,
        ...screenSpaceViewNormalDefineDefaults,
        MAX_STEPS: 10,
        STRIDE: 16,
        FINE_GRAINED_STRIDE: 4,
        BACKFACE_DEPTH_BUFFER_ENABLED: 1,
        OUTPUT: 0,
      },

      uniforms: {
        ...screenSpaceViewPositionUniformDefaults,
        ...screenSpaceViewNormalUniformDefaults,

        sourceBuffer: { value: null },
        backfaceDepthBuffer: { value: null },

        intensity: { value: 0.5 },
        resolution: { value: new THREE.Vector2() },
        assumedThickness: { value: 1 },
        jitter: { value: 1 },
        randomSeed: { value: 0 },
        maxDistance: { value: 100 },
      },

      vertexShader: UVVertexShader,
      fragmentShader: `
        #include <common>
        #include <packing>
        varying vec2 vUv;

        uniform sampler2D sourceBuffer;

        ${screenSpaceViewPositionGLSL}
        ${screenSpaceViewNormalGLSL}

      #if BACKFACE_DEPTH_BUFFER_ENABLED == 1
        uniform sampler2D backfaceDepthBuffer;
      #else
        uniform float assumedThickness;
      #endif

        uniform vec2 resolution;

        uniform float jitter;
        uniform float randomSeed;
        uniform float maxDistance;
        uniform float intensity;

        vec3 Deproject(vec3 p) {
          vec4 res = cameraInverseProjectionMatrix * vec4(p, 1);
          return res.xyz / res.w;
        }

        bool doesIntersect(float rayzmax, float rayzmin, vec2 uv) {

          float sceneZMin = getViewZ(GET_DEPTH(tDepth, uv));
        #if BACKFACE_DEPTH_BUFFER_ENABLED == 1
          float sceneZMax = texture2D(backfaceDepthBuffer, uv).r;
        #else
          float sceneZMax = sceneZMin - assumedThickness;
        #endif

          return rayzmin >= sceneZMax && rayzmax <= sceneZMin;

        }

        float distanceSquared(vec2 a, vec2 b) { a -= b; return dot(a, a); }

        // NOTE: "further" is actually "more negative"
        void swapIfBigger(inout float a, inout float b) {
          if (a > b) {
            float t = a;
            a = b;
            b = t;
          }
        }

        bool isOutsideUvBounds(float x) { return x < 0.0 || x > 1.0; }
        bool isOutsideUvBounds(vec2 uv) { return isOutsideUvBounds(uv.x) || isOutsideUvBounds(uv.y); }

        void main() {

          // Screen position information
          float nearClip = Deproject(vec3(0, 0, -1)).z;

          // Samples
          vec3 normal = getViewNormal(vUv);

          // View space information
          vec3 startViewPos = getViewPosition(vUv, getViewZ(GET_DEPTH(tDepth, vUv)));
          vec3 dir = normalize(reflect(normalize(startViewPos), normalize(normal)));

          // Define view space values
          float maxDist = maxDistance;
          float rayLength = (startViewPos.z + dir.z * maxDist) > nearClip ? (nearClip - startViewPos.z) / dir.z : maxDist;
          vec3 endViewPos = startViewPos + dir * rayLength;

          vec4 H0 = cameraProjectionMatrix * vec4(startViewPos, 1.0);
          vec4 H1 = cameraProjectionMatrix * vec4(endViewPos, 1.0);

          float k0 = 1.0 / H0.w;
          float k1 = 1.0 / H1.w;

          vec3 Q0 = startViewPos * k0;
          vec3 Q1 = endViewPos * k1;

          vec2 P0 = H0.xy * k0;
          vec2 P1 = H1.xy * k1;
          P0 = (P0 * 0.5 + 0.5) * resolution;
          P1 = (P1 * 0.5 + 0.5) * resolution;

          P1 += vec2((distanceSquared(P0, P1) < 0.0001) ? 0.01 : 0.0);

          // Derivatives
          vec2 dP = P1 - P0;
          float greaterDelta = dP.x;
          if (abs(dP.y) > abs(dP.x)) {
            greaterDelta = dP.y;
          }
          vec3 dQ = Q1 - Q0;
          float dk = k1 - k0;

          // Track all values in a vector
          vec4 PQK = vec4(P0, Q0.z, k0);
          vec4 dPQK = vec4(dP, dQ.z, dk) / abs(greaterDelta); // 1 pixel step
          float stepLimit = abs(greaterDelta) / float(STRIDE);

          float jitterRand = rand( vUv + randomSeed );
          PQK += dPQK * float(STRIDE) * jitterRand * jitter;

          // Variables for completion condition
          float prevZMaxEstimate = PQK.z / PQK.w;
          float rayZMin = prevZMaxEstimate, rayZMax = prevZMaxEstimate;
          float stepped = 0.0;

          vec2 hitUV;
          bool intersected = false;
          for (float stepCount = 0.0; stepCount < float(MAX_STEPS); stepCount ++) {

            PQK += dPQK * float(STRIDE);

            rayZMin = prevZMaxEstimate;
            rayZMax = (dPQK.z * float(STRIDE) * 0.5 + PQK.z) / (dPQK.w * float(STRIDE) * 0.5 + PQK.w);
            prevZMaxEstimate = rayZMax;

            // "furter" is "more negative", so max should be further away,
            // or the smaller number
            swapIfBigger(rayZMax, rayZMin);

            stepped = stepCount;
            hitUV = PQK.xy / resolution;
            if (isOutsideUvBounds(hitUV)) break;

            intersected = doesIntersect(rayZMax, rayZMin, hitUV);

            if (intersected || stepCount > stepLimit) break;
          }

          if ( STRIDE > FINE_GRAINED_STRIDE ) {
            if ( intersected ) {
              // Perform a more fine-grained search within the last strided section.
              PQK -= dPQK * float(STRIDE);
            } else {
              // We may have overshot the reflected area right at the beginning of iteration.
              // Perform a more fine-grained search near the ray origin.
              PQK = vec4(P0, Q0.z, k0);
              PQK += dPQK * float(FINE_GRAINED_STRIDE - 1) * jitterRand * jitter;
              stepped = 0.0;
            }

            prevZMaxEstimate = PQK.z / PQK.w;
            rayZMin = prevZMaxEstimate, rayZMax = prevZMaxEstimate;

            for(int j = 0; j < int(float(STRIDE - 1) / float(FINE_GRAINED_STRIDE)); j ++) {
              PQK += dPQK * float(FINE_GRAINED_STRIDE);

              rayZMin = prevZMaxEstimate;
              rayZMax = (dPQK.z * float(FINE_GRAINED_STRIDE) * 0.5 + PQK.z) / (dPQK.w * float(FINE_GRAINED_STRIDE) * 0.5 + PQK.w);
              prevZMaxEstimate = rayZMax;

              swapIfBigger(rayZMax, rayZMin);

              stepped += float(FINE_GRAINED_STRIDE) / float(STRIDE);

              vec2 newUV = PQK.xy / resolution;
              if (doesIntersect(rayZMax, rayZMin, newUV)) {
                intersected = true;
                hitUV = newUV;
                break;
              }

            }
          }

        #if OUTPUT == 0
          vec4 result = texture2D(sourceBuffer, vUv);
        #else
          vec4 result = vec4(0.0);
        #endif
          if (intersected) {

            vec2 ndc = abs(hitUV * 2.0 - 1.0);
            float maxndc = max(ndc.x, ndc.y);
            float fadeVal =
              (1.0 - (max( 0.0, maxndc - 0.4) / (1.0 - 0.4)  )) *
              (1.0 - (stepped / float(MAX_STEPS)));

            // TODO: Add z fade towards camera

            vec4 col = texture2D(sourceBuffer, hitUV, 10.);
        #if OUTPUT == 0
            col.a = intensity * fadeVal;
            result = mix(result, col, col.a);
        #else
            result = vec4( col.rgb, fadeVal );
        #endif
          }

          gl_FragColor = result;

        }
        `,
    });
  }
}
